// File: src/components/MatchFlow/EndMatchBottomSheet.jsx
import React, { useEffect, useRef } from 'react';
import { LuTriangleAlert, LuX } from 'react-icons/lu';
import { useMatchFlow } from './useMatchFlow';

const isPWA = () =>
  (window.matchMedia?.('(display-mode: standalone)').matches) ||
  window.navigator.standalone === true;

export default function EndMatchBottomSheet({ visible, onCancel, onConfirm, fromInSession }) {
  const { socket, match } = useMatchFlow();
  const scrollYRef = useRef(0);

  // Freeze page + status bar tint (match LogoutBottomSheet behavior)
  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (visible) {
      scrollYRef.current = window.scrollY || window.pageYOffset;
      document.body.style.top = `-${scrollYRef.current}px`;
      document.body.classList.add('overlay-active');
      document.documentElement.classList.add('overlay-active');
      document.getElementById('root')?.classList.add('overlay-active');
      if (meta) meta.content = '#999998';
    } else {
      const y = scrollYRef.current;
      document.body.classList.remove('overlay-active');
      document.documentElement.classList.remove('overlay-active');
      document.getElementById('root')?.classList.remove('overlay-active');
      document.body.style.top = '';
      window.scrollTo(0, y);
      if (meta) meta.content = '#ffffff';
    }
    return () => {
      const y = scrollYRef.current;
      document.body.classList.remove('overlay-active');
      document.documentElement.classList.remove('overlay-active');
      document.getElementById('root')?.classList.remove('overlay-active');
      document.body.style.top = '';
      window.scrollTo(0, y);
      const m = document.querySelector('meta[name="theme-color"]');
      if (m) m.content = '#ffffff';
    };
  }, [visible]);

  if (!visible) return null;

  const handleConfirm = () => {
    // 1) Let parent perform any local actions (e.g., endSession())
    onConfirm?.();

    // 2) Also tell the server I'm ending the session so both sides get routed consistently
    if (socket && match?._id) {
      const userId = localStorage.getItem('userId');
      socket.emit('endSession', {
        matchId: match._id,
        userId,
        fromInSession: !!fromInSession,
      });
    }
    // 3) Do not navigate here; the server's "match_ended" drives the flow
  };

  const handleBackdrop = () => onCancel?.();

  return (
    <>
      <div style={S.backdrop} onClick={handleBackdrop}>
        <div
          role="dialog"
          aria-modal="true"
          aria-label="End match confirmation"
          style={{
            ...S.sheet,
            paddingBottom: `calc(12px + env(safe-area-inset-bottom,0px) + ${isPWA() ? 8 : 0}px)`,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Top bar */}
          <div style={S.topBar}>
            <div style={S.pill}>Confirm</div>
            <button aria-label="Close" style={S.iconBtn} onClick={onCancel}>
              <LuX size={18} style={{ display: 'block' }} />
            </button>
          </div>

          {/* Icon + copy (updated visual language) */}
          <div style={S.hero}>
            <div style={{ ...S.iconWrap, background: '#FEF2F2', color: '#B91C1C', borderColor: '#FEE2E2' }}>
              <LuTriangleAlert size={28} />
            </div>
            <h3 style={S.title}>End this match?</h3>
            <p style={S.desc}>
              If you end now you may not be connected with your partner again.
            </p>
          </div>

          {/* CTA row */}
          <div style={S.ctaRow}>
            <button type="button" onClick={onCancel} style={S.secondaryBtn}>
              Cancel
            </button>
            <button type="button" onClick={handleConfirm} style={S.destructiveBtn}>
              Confirm
            </button>
          </div>

          <style>{`
            @keyframes slideUp { from { transform: translateY(12px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
            @media (prefers-reduced-motion: reduce) { * { animation: none !important; transition: none !important } }
            button:focus-visible { outline: 0; box-shadow: 0 0 0 3px rgba(59,130,246,.45) }
          `}</style>
        </div>
      </div>
    </>
  );
}

/* ───────────────────────── Styles (Uber-like tokens; match LogoutBottomSheet) */
const S = {
  backdrop: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.40)',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    padding: 8,
    fontFamily: "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },
  sheet: {
    width: '100%',
    maxWidth: 440,
    background: '#ffffff',
    borderRadius: 12,
    boxShadow: '0 8px 28px rgba(0,0,0,0.25)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    animation: 'slideUp .22s ease-out',
    padding: 16,
    minHeight: 'clamp(340px, 40vh, 520px)',
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 40,
  },
  pill: {
    background: '#f3f4f6',
    border: '1px solid #e5e7eb',
    color: '#0b0b0b',
    borderRadius: 999,
    padding: '6px 10px',
    fontSize: 12,
    fontWeight: 800,
  },
  iconBtn: {
    background: 'transparent',
    border: '1px solid #e5e7eb',
    width: 32, height: 32,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 0,
    padding: 0,
    borderRadius: 8,
    color: '#0b0b0b',
    cursor: 'pointer',
  },
  hero: {
    textAlign: 'center',
    padding: '16px 8px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
  },
  iconWrap: {
    width: 56, height: 56, borderRadius: 999,
    margin: '6px auto 10px',
    display: 'grid', placeItems: 'center',
    background: '#F3F6FF',
    color: '#2553E6',
    border: '1px solid #E5E7EB',
  },
  title: {
    margin: 0,
    fontSize: 18,
    lineHeight: 1.25,
    fontWeight: 900,
    color: '#0b0b0b',
  },
  desc: {
    margin: '6px auto 0',
    fontSize: 14,
    lineHeight: 1.5,
    color: '#4b5563',
    maxWidth: 360,
  },
  ctaRow: {
    display: 'flex',
    gap: 8,
    marginTop: 'auto',
  },
  secondaryBtn: {
    flex: 1,
    background: '#ffffff',
    color: '#0b0b0b',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    padding: '12px 14px',
    fontSize: 13,
    fontWeight: 900,
    cursor: 'pointer',
  },
  destructiveBtn: {
    flex: 1,
    background: '#B91C1C',
    color: '#ffffff',
    border: '1px solid #B91C1C',
    borderRadius: 10,
    padding: '12px 14px',
    fontSize: 13,
    fontWeight: 900,
    cursor: 'pointer',
  },
};
